import type DeviceTemplate from "../../models/DeviceTemplate";
import type TemplateGroup from "../../models/TemplateGroup";

export interface TemplateSelectionOption {
  value: string;
  label: string;
  depth: number;
  expandable?: boolean;
  template?: DeviceTemplate;
  /** The sole direct template is represented by its parent category. */
  representsTemplate?: boolean;
}

export function templateSelectionOptions(
  root: TemplateGroup | undefined,
  current: DeviceTemplate | undefined,
  hierarchical = true,
): TemplateSelectionOption[] {
  const options: TemplateSelectionOption[] = [];
  function visit(group: TemplateGroup, depth: number) {
    const templates = group.allTemplates();
    const direct = group.templates.sortBy("name");
    const groupIndex = options.length;
    options.push({
      value: `group:${group.id}`, label: group.name, depth,
      template: group.favoriteTemplate,
      representsTemplate: templates.length === 1 && direct.length === 1,
    });
    if (direct.length > 1 || (direct.length === 1 && templates.length > 1)) {
      for (const template of direct) {
        options.push({ value: template.id, label: template.name, depth: depth + 1, template, representsTemplate: true });
      }
    }
    for (const child of group.templateGroups.sortBy("tag")) visit(child, depth + 1);
    options[groupIndex].expandable = options.length > groupIndex + 1;
  }
  if (root) {
    if (hierarchical) {
      // The caller already fixes the root category; only its contents are selectable.
      for (const template of root.templates.sortBy("name")) {
        options.push({ value: template.id, label: template.name, depth: 0, template, representsTemplate: true });
      }
      for (const child of root.templateGroups.sortBy("tag")) visit(child, 0);
    }
    else {
      for (const template of root.allTemplates().sort((a, b) => a.name.localeCompare(b.name))) {
        options.push({ value: template.id, label: template.name, depth: 0, template, representsTemplate: true });
      }
    }
  }
  if (current && !options.some(option => option.representsTemplate && option.template?.id === current.id)) {
    options.unshift({ value: current.id, label: current.name, depth: 0, template: current, representsTemplate: true });
  }
  return options;
}

export function selectedTemplateOption(options: TemplateSelectionOption[], templateId: string): string {
  return options.find(option => option.representsTemplate && option.template?.id === templateId)?.value ?? "select";
}

/** Keep the complete option list for resolving the selected value, even when its parent is collapsed. */
export function visibleTemplateOptions(options: TemplateSelectionOption[], collapsed: ReadonlySet<string>): TemplateSelectionOption[] {
  let hiddenBelow: number | undefined;
  return options.filter(option => {
    if (hiddenBelow !== undefined && option.depth > hiddenBelow) return false;
    hiddenBelow = option.expandable && collapsed.has(option.value) ? option.depth : undefined;
    return true;
  });
}
